import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";
import { Search } from "lucide-react";
import { cn } from "@/utils";

/**
 * SearchBox
 *
 * A search input with a leading magnifier and an optional trailing hint
 * (typically a {@link Kbd} shortcut). Purely presentational — filtering logic
 * lives with the caller.
 */
export interface SearchBoxProps extends InputHTMLAttributes<HTMLInputElement> {
  hint?: ReactNode;
}

export const SearchBox = forwardRef<HTMLInputElement, SearchBoxProps>(
  ({ hint, className, placeholder = "Search…", ...rest }, ref) => (
    <div className="relative flex items-center">
      <Search
        size={14}
        className="pointer-events-none absolute left-2.5 text-text-tertiary"
        aria-hidden
      />
      <input
        ref={ref}
        type="search"
        role="searchbox"
        placeholder={placeholder}
        className={cn(
          "h-8 w-full rounded-md border border-border bg-bg-elevated pl-8 pr-3 text-base",
          "text-text-primary placeholder:text-text-tertiary transition-colors duration-fast",
          "hover:border-border-strong focus:border-border-strong selectable",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus",
          "[&::-webkit-search-cancel-button]:appearance-none",
          hint && "pr-10",
          className,
        )}
        {...rest}
      />
      {hint && (
        <span className="pointer-events-none absolute right-2.5">{hint}</span>
      )}
    </div>
  ),
);

SearchBox.displayName = "SearchBox";
